/* eslint-disable */
// "Made from recipe & combos" tab: can-be-made stock and menu on/off for recipe items and combos.
// Data: GET /v2.1/inventory/derivedStock (console2Backend derivedStock.py).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import moment from 'moment';
import { Dialog, DialogActions, DialogContent, DialogTitle, Switch, Tooltip } from '@mui/material';
import { RefreshCcw, HelpCircle } from 'lucide-react';
import { notificationServices } from '@/Services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import BaseDrawer from '@/Components/Drawers/BaseDrawer/BaseDrawer';

const ROW_HEIGHT = 52;
const GRID = 'minmax(220px,2.2fr) 80px 110px 100px minmax(140px,1.2fr) 100px minmax(240px,1.6fr) 110px 80px';

const fmt = (n, unit) => {
	const v = Number(n ?? 0);
	const shown = unit === 'count' ? Math.floor(v) : Math.round(v * 1000) / 1000;
	return `${shown} ${unit === 'count' ? '' : unit || ''}`.trim();
};

const PILL = {
	auto: 'bg-green-100 text-green-800',
	hand: 'bg-violet-100 text-violet-800',
	wait: 'bg-amber-100 text-amber-800',
	off: 'bg-gray-200 text-gray-600',
};

// What the Status cell says for a row (wording kept in one place).
const statusOf = (r, canSwitch) => {
	if (!canSwitch) return { pill: 'off', label: 'Menu switching is off for this store' };
	if (r.waitingForPartner) return { pill: 'wait', label: `Turn-${r.desiredActive ? 'on' : 'off'} sent`, note: "partner hasn't confirmed yet" };
	if (r.manualOverride) return { pill: 'hand', label: 'Set by hand', note: 'stays until you undo', backToAuto: true };
	return { pill: 'auto', label: 'Auto', note: 'follows stock' };
};

const notify = (type, message) => notificationServices.generateNotification({ type, message });

const DerivedStockTab = ({ storeId, search = '' }) => {
	const [data, setData] = useState({ enabled: true, rows: [] });
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState({}); // variantId | 'ALL' -> true
	const [typeFilter, setTypeFilter] = useState('ALL');
	const [why, setWhy] = useState(null); // { row, data, loading }
	const [confirm, setConfirm] = useState(null); // { title, text, okLabel, onOk }

	// stable row type (latest renderer via ref) so react-window updates rows instead of remounting them
	const renderRow = useRef();
	const Row = useCallback((p) => renderRow.current(p), []);
	const storeRef = useRef(storeId);
	storeRef.current = storeId;
	const load = useCallback(async () => {
		if (!storeId) return;
		setLoading(true);
		try {
			const res = await inventoryAnalyticsServices.fetchDerivedStock({ data: { storeId } });
			if (storeRef.current !== storeId) return; // a newer store was picked
			if (res?.statusCode === 200) setData(res.data);
			else throw new Error(res?.errorMessage || res?.message);
		} catch (e) {
			notify('error', e?.message || 'Could not load recipe & combo stock');
		} finally {
			setLoading(false);
		}
	}, [storeId]);

	useEffect(() => {
		load();
	}, [load]);
	// leaving the tab with the Why panel open would keep its page-scroll lock; release it
	useEffect(() => () => { document.body.style.overflow = ''; }, []);

	const run = async (action, key, body, okMsg) => {
		setBusy((b) => ({ ...b, [key]: true }));
		try {
			const res = await inventoryAnalyticsServices.derivedStockAction(action, { data: { storeId, ...body } });
			if (res?.statusCode !== 200) throw new Error(res?.errorMessage || res?.message);
			if (storeRef.current !== storeId) return; // store changed while it ran
			setData(res.data);
			const results = Object.values(res.data?.result || {});
			const notSent = results.filter((r) => String(r) !== 'sent').length;
			notify(notSent ? 'warning' : 'success', notSent ? `${okMsg}. ${notSent} item(s) not switched - see status` : okMsg);
		} catch (e) {
			notify('error', e?.message || 'Something went wrong');
			load();
		} finally {
			setBusy((b) => ({ ...b, [key]: false }));
		}
	};

	const recalcAll = () => run('RECALCULATE', 'ALL', {}, 'Numbers recalculated');
	const allBackToAuto = () => setConfirm({
		title: 'Put every item back to auto?',
		text: 'Each item will be shown or hidden based on what can be made now. Items set by hand go back to following stock.',
		okLabel: 'All back to auto',
		onOk: () => run('TOGGLE', 'ALL', {}, 'Menu updated'),
	});

	const setByHand = (r, on) => setConfirm({
		title: on ? `Show ${r.name} on the menu?` : `Hide ${r.name} from the menu?`,
		text: on
			? "It will stay on, even when it can't be made, until you press Back to auto."
			: 'It will stay off, even when stock comes back, until you press Back to auto.',
		okLabel: on ? 'Show' : 'Hide',
		onOk: () => {
			// move the switch now; the server reply (or a reload on error) settles it
			setData((d) => ({ ...d, rows: d.rows.map((x) => (x.variantId === r.variantId ? { ...x, desiredActive: on, manualOverride: true } : x)) }));
			run('ACTIVE', r.variantId, { variantId: r.variantId, active: on }, on ? 'Shown on menu' : 'Hidden from menu');
		},
	});

	const backToAuto = (r) => setConfirm({
		title: 'Let stock decide again?',
		text: `${r.name} will be turned ${r.availableQty > 0 ? 'on' : 'off'} now (you can make ${fmt(r.availableQty, r.unit)}).`,
		okLabel: 'Back to auto',
		onOk: () => run('TOGGLE', r.variantId, { variantIds: [r.variantId] }, 'Back to auto'),
	});

	const openWhy = async (row) => {
		setWhy({ row, loading: true });
		try {
			const res = await inventoryAnalyticsServices.fetchMakeableDebug({ params: { variantId: row.variantId, storeId } });
			setWhy({ row, data: res?.data, loading: false });
		} catch (e) {
			setWhy({ row, error: e?.message, loading: false });
		}
	};

	const rows = useMemo(() => {
		const q = search.toLowerCase().trim();
		return (data?.rows || []).filter((r) => {
			if (typeFilter !== 'ALL' && r.stockType !== typeFilter) return false;
			return !q || (r.name || '').toLowerCase().includes(q);
		});
	}, [data, search, typeFilter]);

	const tiles = useMemo(() => {
		const all = data?.rows || [];
		return [
			['Items', all.length, 'text-gray-900'],
			['On menu', all.filter((r) => r.desiredActive).length, 'text-green-700'],
			['Off menu', all.filter((r) => !r.desiredActive).length, 'text-red-700'],
			['Set by hand', all.filter((r) => r.manualOverride).length, 'text-violet-700'],
		];
	}, [data]);

	if (data && data.enabled === false) {
		return (
			<div className='p-6 border rounded-lg bg-gray-50 text-gray-700'>
				Recipe & combo stock is off for this store. Turn on
				<b> "Work out recipe items & combos stock"</b> in the store's Settings.
			</div>
		);
	}

	renderRow.current = ({ index, style }) => {
		const r = rows[index];
		const canSwitch = data?.canSwitch !== false;
		const st = statusOf(r, canSwitch);
		return (
			<div
				style={{ ...style, display: 'grid', gridTemplateColumns: GRID }}
				className={`items-center text-sm border-b border-gray-100 px-3 ${index % 2 ? '' : 'bg-gray-50/60'}`}
			>
				<div className='truncate font-medium text-gray-900' title={r.name}>
					{r.name}
				</div>
				<div>
					<span className={`text-xs rounded px-2 py-0.5 ${r.stockType === 'COMBO' ? 'bg-purple-100 text-purple-800' : 'bg-sky-100 text-sky-800'}`}>
						{r.stockType === 'COMBO' ? 'Combo' : 'Recipe'}
					</span>
				</div>
				<div className={`font-semibold ${r.availableQty > 0 ? 'text-gray-900' : 'text-red-700'}`}>{fmt(r.availableQty, r.unit)}</div>
				<div className='text-gray-600'>{r.pendingSalesQty ? fmt(r.pendingSalesQty, r.unit) : '-'}</div>
				<div className='truncate text-gray-600' title={r.limitingName}>{r.limitingName || '-'}</div>
				<div className='flex items-center gap-1'>
					<Tooltip title='Show or hide this item on the menu'>
						<span>
							<Switch size='small' checked={Boolean(r.desiredActive)} inputProps={{ 'aria-label': `On menu: ${r.name}` }}
								disabled={!canSwitch || busy[r.variantId]}
								onChange={(e) => setByHand(r, e.target.checked)} />
						</span>
					</Tooltip>
					<span className='text-xs font-semibold text-gray-700'>{r.desiredActive ? 'On' : 'Off'}</span>
				</div>
				<div className='flex items-center gap-2 text-xs min-w-0'>
					<span className={`rounded-full px-2 py-0.5 font-semibold whitespace-nowrap ${PILL[st.pill]}`}>{st.label}</span>
					{st.note && <span className='text-gray-500 truncate'>{st.note}</span>}
					{st.backToAuto && (
						<Tooltip title='Let stock decide again'>
							<button type='button' disabled={busy[r.variantId]} onClick={() => backToAuto(r)}
								className='font-semibold text-blue-700 hover:underline whitespace-nowrap disabled:opacity-40'>
								Back to auto
							</button>
						</Tooltip>
					)}
				</div>
				<div className='text-xs text-gray-500'>{r.lastComputedAt ? moment.min(moment.utc(r.lastComputedAt).local(), moment()).fromNow() : '-'}</div>
				<div className='flex items-center gap-1'>
					<Tooltip title="Recount how many can be made. Doesn't change the menu.">
						<button type='button' disabled={busy[r.variantId]} className='p-1.5 rounded hover:bg-gray-200 disabled:opacity-40'
							onClick={() => run('RECALCULATE', r.variantId, { variantIds: [r.variantId] }, 'Recalculated')}>
							<RefreshCcw size={15} className={busy[r.variantId] ? 'animate-spin' : ''} />
						</button>
					</Tooltip>
					<Tooltip title='See which ingredient is limiting this item'>
						<button type='button' className='p-1.5 rounded hover:bg-gray-200' onClick={() => openWhy(r)}>
							<HelpCircle size={15} />
						</button>
					</Tooltip>
				</div>
			</div>
		);
	};

	return (
		<div className='flex flex-col gap-3'>
			<div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
				{tiles.map(([label, n, cls]) => (
					<div key={label} className='border rounded-lg p-3 bg-white'>
						<div className='text-xs text-gray-500'>{label}</div>
						<div className={`text-2xl font-semibold ${cls}`}>{n}</div>
					</div>
				))}
			</div>

			<div className='flex flex-wrap items-center gap-2'>
				<select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className='border rounded-md px-2 py-1.5 text-sm'>
					<option value='ALL'>All types</option>
					<option value='RECIPE'>Recipe items</option>
					<option value='COMBO'>Combos</option>
				</select>
				<span className='text-xs text-gray-500'>{rows.length} shown{data?.orderAware ? ' · promised orders are taken off' : ''}</span>
				<div className='flex-1' />
				<button type='button' disabled={busy.ALL || loading} onClick={recalcAll}
					className='px-3 py-1.5 rounded-md border text-sm font-semibold disabled:opacity-40'>
					{busy.ALL ? 'Working…' : 'Recalculate all'}
				</button>
				<button type='button' disabled={busy.ALL || loading || data?.canSwitch === false} onClick={allBackToAuto}
					className='px-3 py-1.5 rounded-md bg-uy-color text-white text-sm font-semibold disabled:opacity-40'>
					All back to auto
				</button>
			</div>
			<div className='flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600'>
				<span><b className='text-gray-800'>On menu</b> switch: show or hide the item for customers</span>
				<span><span className={`rounded-full px-2 py-0.5 font-semibold ${PILL.auto}`}>Auto</span> follows stock by itself</span>
				<span><span className={`rounded-full px-2 py-0.5 font-semibold ${PILL.hand}`}>Set by hand</span> stays as you set it until you press <b className='text-gray-800'>Back to auto</b></span>
				<span className='flex items-center gap-1'><RefreshCcw size={13} /> Recount (menu not changed)</span>
				<span className='flex items-center gap-1'><HelpCircle size={13} /> See what's limiting it</span>
			</div>

			<div className='border rounded-lg overflow-x-auto bg-white'>
				<div style={{ minWidth: 1180 }}>
				<div style={{ display: 'grid', gridTemplateColumns: GRID }} className='px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-600 uppercase'>
					<div>Item</div><div>Type</div><div>Can make</div><div>Promised</div><div>Short on</div><div>On menu</div><div>Status</div><div>Updated</div><div />
				</div>
				{loading ? (
					<div className='p-6 text-sm text-gray-500'>Loading…</div>
				) : rows.length === 0 ? (
					<div className='p-6 text-sm text-gray-500'>
						{data?.rows?.length ? 'No items match the search' : 'No numbers yet. Press "Recalculate all" to fill the list.'}
					</div>
				) : (
					<FixedSizeList height={Math.min(rows.length * ROW_HEIGHT, Math.max(window.innerHeight - 380, 320))}
						itemCount={rows.length} itemData={{ rows, busy }} itemSize={ROW_HEIGHT} width='100%' overscanCount={8}>
						{Row}
					</FixedSizeList>
				)}
				</div>
			</div>

			<Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth='xs' fullWidth>
				<DialogTitle>{confirm?.title}</DialogTitle>
				<DialogContent className='text-sm text-gray-600'>{confirm?.text}</DialogContent>
				<DialogActions>
					<button type='button' className='px-3 py-1.5 rounded-md border text-sm font-semibold' onClick={() => setConfirm(null)}>Cancel</button>
					<button type='button' className='px-3 py-1.5 rounded-md bg-uy-color text-white text-sm font-semibold'
						onClick={() => { const { onOk } = confirm; setConfirm(null); onOk(); }}>
						{confirm?.okLabel}
					</button>
				</DialogActions>
			</Dialog>
			<BaseDrawer open={Boolean(why)} onClose={() => setWhy(null)} header={`Why: ${why?.row?.name || ''}`} size='sm'>
				<WhyPanel why={why} />
			</BaseDrawer>
		</div>
	);
};

const WhyPanel = ({ why }) => {
	if (!why) return null;
	if (why.loading) return <div className='p-4 text-sm text-gray-500'>Loading…</div>;
	if (why.error) return <div className='p-4 text-sm text-red-600'>{why.error}</div>;
	const d = why.data || {};
	if (d.combo) {
		return (
			<div className='p-4 flex flex-col gap-2 text-sm'>
				<div>One combo needs:</div>
				{d.combo.items.map((i) => (
					<div key={i.variantId} className={`flex justify-between border-b py-1 ${i.variantId === d.combo.limitingVariantId ? 'font-semibold text-red-700' : ''}`}>
						<span>{i.productName} {i.variantName}</span>
						<span>{i.qtyPerCombo} each · {i.currentStock} in stock → {Math.floor(Math.max(i.currentStock, 0) / i.qtyPerCombo)} combos</span>
					</div>
				))}
				<div className='mt-2 font-semibold'>Can make: {d.combo.makeableQty}</div>
			</div>
		);
	}
	return (
		<div className='p-4 flex flex-col gap-3 text-sm'>
			{d.problems?.length > 0 && (
				<div className='bg-amber-50 border border-amber-200 rounded p-2'>
					{d.problems.map((p) => <div key={p}>• {p}</div>)}
				</div>
			)}
			{d.ingredients?.length > 0 && <div>One item needs:</div>}
			{(d.ingredients || []).map((i) => (
				<div key={i.variantId} className={`flex justify-between gap-3 border-b py-1 ${i.variantId === d.limitingIngredient?.variantId ? 'font-semibold text-red-700' : ''}`}>
					<span>{i.name}</span>
					<span className='text-right whitespace-nowrap'>
						{i.needPerFGUnit} {i.unit || ''} each · {i.storeStock} in stock → {i.canMakeByThis == null ? '-' : Math.floor(i.canMakeByThis)}
						{i.note && <div className='text-[11px] font-normal text-gray-500'>{i.note}</div>}
					</span>
				</div>
			))}
			{d.makeableQty != null && <div className='mt-2 font-semibold'>Can make: {d.makeableQty}</div>}
		</div>
	);
};

export default DerivedStockTab;
